import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { Icons } from '../components/Icons';
import './OrganizationSelector.css';
import { apiFetch } from '../utils/api';

const INITIAL_FORM_STATE = {
  name: '', business_number: '', address: '', city: '',
  province: 'ON', postal_code: '', phone: '', contact_person: '',
  mobile: '', email: '', fiscal_year_end: '', industry: 'General',
};
function OrganizationSelector({ selectedOrg, onSelectOrg, tenantId }) {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(true);

  const triggerNotification = (message, type = 'success') => {
    const options = { position: "top-right", autoClose: 5000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true };
    switch (type) {
      case 'success': toast.success(message, options); break;
      case 'danger': toast.error(message, options); break;
      case 'warning': toast.warn(message, options); break;
      case 'info': toast.info(message, options); break;
      default: toast(message, options); break;
    }
  };

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/organizations', tenantId);
      setOrganizations(data || []);
    } catch (error) {
      triggerNotification('Error fetching organizations data. Please try refreshing.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Lock background scrolling when drawer is open
  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showForm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlaceSelected = (placeData) => {
    setFormData(prev => ({
      ...prev,
      address: placeData.address || prev.address,
      city: placeData.city || prev.city,
      province: placeData.province || prev.province,
      postal_code: placeData.postal_code || prev.postal_code
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = editingId ? `/organizations/${editingId}` : '/organizations';
    const method = editingId ? 'PUT' : 'POST';
    const body = { ...formData, industry: formData.industry || 'General' };

    try {
      await apiFetch(endpoint, tenantId, { method, body });
      triggerNotification(`Organization successfully ${editingId ? 'updated' : 'created'}.`, 'success');
      resetForm();
      fetchOrganizations();
    } catch (error) {
      triggerNotification('Network error. Please check your connection.', 'danger');
    }
  };

  const handleEdit = (org) => {
    setFormData({ ...org, industry: org.industry || 'General' });
    setEditingId(org.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this organization?')) return;
    
    try {
      await apiFetch(`/organizations/${id}`, tenantId, { method: 'DELETE' });
      triggerNotification('Organization deactivated successfully.', 'success');
      fetchOrganizations();
    } catch (error) {
      triggerNotification('Could not deactivate organization.', 'danger');
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setEditingId(null);
    setShowForm(false);
  };

  const filteredOrganizations = organizations.filter(org =>
    (org?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (org?.business_number || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container-fluid py-4 px-md-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Add ToastContainer here. Ideally, this should be in your root App.js */}
      <ToastContainer />

      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center">
            <Icons.Building />
            <h1 className="h4 fw-bold text-dark mb-0">Org Management</h1>
          </div>
          <p className="text-muted small mb-0 mt-1">Manage global organization configurations and legal profiles.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center justify-content-center fw-semibold px-4 shadow-sm"
          onClick={() => setShowForm(true)}
        >
          <Icons.Plus /> Create New Organization
        </button>
      </div>

      <div className="row g-4">
        {/* Full-Width Directory Grid */}
        <div className="col-12">
          <div className="card border-0 shadow-sm rounded-3 bg-white">
            <div className="p-3 p-md-4 border-bottom d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3">
              <h2 className="h6 fw-bold mb-0 text-dark">Organizations Directory</h2>
              <div className="position-relative" style={{ minWidth: '260px' }}>
                <Icons.Search />
                <input
                  type="text"
                  className="form-control bg-light border-0 ps-5 shadow-none"
                  placeholder="Search by name or number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="card-body p-0">
              {loading ? (
                <div className="p-5 text-center text-muted d-flex flex-column align-items-center">
                  <div className="spinner-border text-primary mb-3" role="status"></div>
                  <span>Loading directory data...</span>
                </div>
              ) : filteredOrganizations.length === 0 ? (
                <div className="p-5 text-center text-muted">
                  No organizations found matching your criteria.
                </div>
              ) : (
                <>
                  {/* Mobile Card View (hidden on sm and up) */}
                  <div className="d-sm-none">
                    {filteredOrganizations.map(org => (
                      <div key={org.id} className={`org-card ${selectedOrg?.id === org.id ? 'selected' : ''}`}>
                        <div className="org-card-header" onClick={() => { onSelectOrg(org, tenantId); navigate('/dashboard'); }}>
                          <div className="org-card-name">{org.name}</div>
                          <span className={`badge rounded-pill fw-medium ${org.is_active !== 0 ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                            {org.is_active !== 0 ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="org-card-body">
                          <div className="org-card-detail">
                            <span className="label">Industry</span>
                            <span className="value">{org.industry || 'General'}</span>
                          </div>
                          <div className="org-card-detail">
                            <span className="label">Business No.</span>
                            <span className="value font-monospace">{org.business_number || '-'}</span>
                          </div>
                        </div>
                        <div className="org-card-actions">
                          <button type="button" className="btn btn-sm btn-light text-primary flex-grow-1" onClick={() => handleEdit(org)}>
                            <Icons.Edit /> Edit
                          </button>
                          <button type="button" className="btn btn-sm btn-light text-danger flex-grow-1" onClick={() => handleDelete(org.id)}>
                            <Icons.Trash /> Deactivate
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tablet/Desktop Table View (hidden below sm) */}
                  <div className="table-responsive d-none d-sm-block">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light text-uppercase text-muted" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                        <tr>
                          <th className="px-4 py-3 border-0">Name</th>
                          <th className="border-0">Industry</th>
                          <th className="border-0">Business No.</th>
                          <th className="border-0 d-none d-md-table-cell">Location</th>
                          <th className="border-0">Status</th>
                          <th className="text-end px-4 border-0">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrganizations.map(org => (
                          <tr key={org.id} className={selectedOrg?.id === org.id ? 'table-primary border-start border-primary border-3' : ''}>
                            <td 
                              className="px-4 py-3 fw-semibold text-primary" 
                              onClick={() => { onSelectOrg(org, tenantId); navigate('/dashboard'); }}
                              style={{ cursor: 'pointer' }}
                              title="Select and load dashboard"
                            >
                              {org.name}
                            </td>
                            <td className="text-muted small">{org.industry || 'General'}</td>
                            <td className="text-muted small font-monospace">{org.business_number || '-'}</td>
                            <td className="small d-none d-md-table-cell">{org.city}, {org.province}</td>
                            <td>
                              <span className={`badge rounded-pill px-3 py-1 fw-medium ${org.is_active !== 0 ? 'bg-success bg-opacity-10 text-success' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
                                {org.is_active !== 0 ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="text-end px-4 text-nowrap">
                              <button type="button" className="btn btn-sm btn-light text-primary me-2" onClick={() => handleEdit(org)}>
                                <Icons.Edit /> <span className="d-none d-md-inline">Edit</span>
                              </button>
                              <button type="button" className="btn btn-sm btn-light text-danger" onClick={() => handleDelete(org.id)}>
                                <Icons.Trash /> <span className="d-none d-md-inline"> </span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Side Drawer Overlay for Create/Edit */}
      {showForm && (
        <>
          {/* Backdrop */}
          <div 
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark opacity-50" 
            style={{ zIndex: 1040, transition: 'opacity 0.3s' }} 
            onClick={resetForm}
          ></div>
          
          {/* Sliding Panel */}
          <div 
            className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-flex flex-column"
            style={{ 
              width: '100%', 
              maxWidth: '550px', 
              zIndex: 1050,
              animation: 'slideInRight 0.3s ease-out forwards'
            }}
          >
            <style>
              {`
                @keyframes slideInRight {
                  from { transform: translateX(100%); }
                  to { transform: translateX(0); }
                }
              `}
            </style>

            {/* Drawer Header */}
            <div className="bg-light border-bottom p-4 d-flex justify-content-between align-items-center">
              <h3 className="h5 fw-bold mb-0 text-dark">{editingId ? 'Edit Organization Profile' : 'Register New Organization'}</h3>
              <button type="button" className="btn-close shadow-none" onClick={resetForm} aria-label="Close"></button>
            </div>

            {/* Drawer Body (Scrollable) */}
            <div className="flex-grow-1 overflow-auto p-4">
              <form id="organization-form" onSubmit={handleSubmit}>
                {/* Basic Details */}
                <h4 className="h6 text-muted mb-3 fw-bold border-bottom pb-2" style={{ fontSize: '0.8rem' }}>PRIMARY DETAILS</h4>
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <label className="form-label small fw-semibold">Organization Name <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Industry</label>
                    <select className="form-select" name="industry" value={formData.industry} onChange={handleInputChange}>
                      <option value="General">General</option>
                      <option value="College/School">College/School</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Business Number <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" name="business_number" value={formData.business_number} onChange={handleInputChange} placeholder="123456789RC0001" required />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold">Fiscal Year End</label>
                    <input type="date" className="form-control" name="fiscal_year_end" value={formData.fiscal_year_end} onChange={handleInputChange} />
                  </div>
                </div>

                {/* Location Details */}
                <h4 className="h6 text-muted mb-3 fw-bold border-bottom pb-2" style={{ fontSize: '0.8rem' }}>LOCATION</h4>
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <label className="form-label small fw-semibold">Address <span className="text-danger">*</span></label>
                    <AddressAutocomplete className="form-control" value={formData.address} onChange={handleInputChange} onPlaceSelected={handlePlaceSelected} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">City <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" name="city" value={formData.city} onChange={handleInputChange} required />
                  </div>
                  <div className="col-md-3 col-6">
                    <label className="form-label small fw-semibold">Province</label>
                    <select className="form-select" name="province" value={formData.province} onChange={handleInputChange} required>
                      <option value="ON">ON</option>
                      <option value="BC">BC</option>
                      <option value="AB">AB</option>
                    </select>
                  </div>
                  <div className="col-md-3 col-6">
                    <label className="form-label small fw-semibold">Postal <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" name="postal_code" value={formData.postal_code} onChange={handleInputChange} required />
                  </div>
                </div>

                {/* Contact Details */}
                <h4 className="h6 text-muted mb-3 fw-bold border-bottom pb-2" style={{ fontSize: '0.8rem' }}>CONTACT INFO</h4>
                <div className="row g-3 mb-2">
                  <div className="col-md-12">
                    <label className="form-label small fw-semibold">Contact Person</label>
                    <input type="text" className="form-control" name="contact_person" value={formData.contact_person} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label small fw-semibold">Email Address</label>
                    <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Office Phone</label>
                    <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Mobile</label>
                    <input type="tel" className="form-control" name="mobile" value={formData.mobile} onChange={handleInputChange} />
                  </div>
                </div>
              </form>
            </div>

            {/* Drawer Footer (Pinned to bottom) */}
            <div className="bg-light p-3 border-top d-flex justify-content-end gap-2 mt-auto">
              <button type="button" className="btn btn-light border fw-semibold flex-grow-1 flex-md-grow-0" onClick={resetForm}>Cancel</button>
              <button type="submit" form="organization-form" className="btn btn-primary fw-semibold px-4 shadow-sm flex-grow-1 flex-md-grow-0">
                {editingId ? 'Save Changes' : 'Create Organization'}
              </button>
            </div>
            
          </div>
        </>
      )}
    </div>
  );
}

export default OrganizationSelector;